import { useEffect, useState } from "react";
import GiftAnimation from "./GiftAnimation";

/*
 * GiftQueue
 * =========
 * Plays a list of gift animations back-to-back in the same z-layer. Consumers
 * push new gifts via the `push` API returned by useGiftQueue().
 *
 * Usage:
 *   const { queue, push, clear } = useGiftQueue();
 *   <GiftQueue queue={queue} onFinish={clear} />
 */
export default function GiftQueue({ queue, onFinish, onAdvance, onDismiss }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // If the queue was emptied externally, reset the pointer.
    if (queue.length === 0) setIdx(0);
    // If a new gift was pushed while we finished, ensure we advance to it.
    if (idx >= queue.length && queue.length > 0) setIdx(queue.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.length]);

  if (!queue.length) return null;
  const current = queue[idx];
  if (!current) return null;

  const handleDone = () => {
    if (idx + 1 >= queue.length) {
      onFinish?.();
    } else {
      setIdx(idx + 1);
      onAdvance?.(idx + 1);
    }
  };

  // User tap → clear the entire remaining queue and let comments interact.
  const handleDismiss = () => {
    setIdx(0);
    (onDismiss || onFinish)?.();
  };

  return (
    <GiftAnimation
      gift={current}
      onDone={handleDone}
      onDismiss={handleDismiss}
    />
  );
}

export function useGiftQueue() {
  const [queue, setQueue] = useState([]);
  return {
    queue,
    push: (gift) => setQueue((q) => [...q, gift]),
    pushAll: (gifts) => setQueue((q) => [...q, ...gifts]),
    clear: () => setQueue([]),
    reset: () => setQueue([]),
  };
}
